import { IApiError } from '@frontend/request';

import {
  ESymbolCategory,
  IDeleteSearchHistoryParams,
  IGetSymbolDetailParams,
  IHideMarketSymbolParams,
  IMarketSymbolCategory,
  ISearchSymbolsParams,
  ISymbol,
  ISymbolDetail,
  marketAPI
} from '@aqx-core/common/api/market';
import { IAPIControllerCallbacks } from '@aqx-core/common/api/types';

import { isDummyOptionSymbol } from './dummy';

class MarketAPIController {
  private symbolDetailAC?: AbortController;
  private symbolOrderDetailAC?: AbortController;
  private symbolSearchAC?: AbortController;

  public hideMarketSymbols = async (params: IHideMarketSymbolParams, callbacks: IAPIControllerCallbacks<never>) => {
    try {
      await marketAPI.hideMarketSymbols(params);
      callbacks.onSuccess();
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public getMarketSymbolCategories = async (callbacks: IAPIControllerCallbacks<IMarketSymbolCategory>) => {
    try {
      const { result } = await marketAPI.marketSymbolCategories();
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public getSymbolDetail = async (
    params: IGetSymbolDetailParams,
    callbacks: IAPIControllerCallbacks<ISymbolDetail>
  ) => {
    try {
      this.symbolDetailAC?.abort();
      this.symbolDetailAC = new AbortController();

      const { result } = await marketAPI.symbolDetail(params, this.symbolDetailAC.signal);

      let resultData = result || null;

      if (resultData && (params?.symbol === 'AAPL' || isDummyOptionSymbol(params?.symbol))) {
        resultData = {
          ...resultData,
          underlyingSymbols: ['AAPL'],
          tradableSecurityTypes: [ESymbolCategory.EQUITIES, ESymbolCategory.OPTION]
        };
      }

      callbacks.onSuccess(resultData);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public getSymbolOrderDetail = async (
    params: IGetSymbolDetailParams,
    callbacks: IAPIControllerCallbacks<ISymbolDetail>
  ) => {
    try {
      this.symbolOrderDetailAC?.abort();
      this.symbolOrderDetailAC = new AbortController();

      const { result } = await marketAPI.symbolOrderDetail(params, this.symbolOrderDetailAC.signal);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  // Get all symbols except hidden symbols by user
  public getMarketSymbolsWithHideEffect = async (callbacks: IAPIControllerCallbacks<ISymbol[]>) => {
    try {
      const { result } = await marketAPI.symbols();
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  // Get all symbols and hidden symbols
  public getAllSymbolsWithoutHideEffect = async (callbacks: IAPIControllerCallbacks<ISymbol[]>) => {
    try {
      const { result } = await marketAPI.allSymbols();
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public searchSymbol = async (params: ISearchSymbolsParams, callbacks: IAPIControllerCallbacks<ISymbol[]>) => {
    try {
      this.symbolSearchAC?.abort();
      this.symbolSearchAC = new AbortController();
      const { result } = await marketAPI.searchSymbol(params, this.symbolSearchAC.signal);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public getSearchHistories = async (callbacks: IAPIControllerCallbacks<ISymbol[]>) => {
    try {
      const { result } = await marketAPI.symbolSearchHistories();
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public deleteSearchHistories = async (
    params: IDeleteSearchHistoryParams | undefined,
    callbacks: IAPIControllerCallbacks<boolean>
  ) => {
    try {
      const { result } = await marketAPI.deleteSearchHistories(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public updateSearchHistories = async (params: ISearchSymbolsParams, callbacks: IAPIControllerCallbacks<boolean>) => {
    try {
      const { result } = await marketAPI.updateSearchHistories(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };
}

export const marketAPIController = new MarketAPIController();
